import {
  defaultLoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import type { Result } from '@carvana/merch-result';
import React, { createContext, useMemo } from 'react';
import { InventoryTypeId } from '~/constants/inventoryType';
import type { DeliveryPickup, LoadableState } from '~/contracts/common';

export type AvailabilityPayloads = {
  vehicleId: number;
  vehicleLocationId: number;
  vehicleInventoryType: InventoryTypeId;
};

type AvailabilityData = {
  soonestPickup: Result<DeliveryPickup>;
  soonestDelivery: Result<DeliveryPickup>;
};

export type PageAvailabilityData = Record<number, AvailabilityData>;

export type AvailabilityContextType = {
  availabilityPayloads: LoadableState<AvailabilityPayloads[]>;
  carouselVehicleAvailability: LoadableState<AvailabilityPayloads[]>;
  pageAvailability: LoadableState<PageAvailabilityData>;
  recentVehicleAvailability: LoadableState<AvailabilityPayloads[]>;
  suggestedVehicleAvailability: LoadableState<AvailabilityPayloads[]>;
};

const defaultContext: AvailabilityContextType = {
  availabilityPayloads: defaultLoadableState<AvailabilityPayloads[]>([]),
  carouselVehicleAvailability: defaultLoadableState<AvailabilityPayloads[]>([]),
  pageAvailability: defaultLoadableState<PageAvailabilityData>({}),
  recentVehicleAvailability: defaultLoadableState<AvailabilityPayloads[]>([]),
  suggestedVehicleAvailability: defaultLoadableState<AvailabilityPayloads[]>([])
};

const AvailabilityContext =
  createContext<AvailabilityContextType>(defaultContext);

export function AvailabilityContextProvider({
  children
}: React.PropsWithChildren) {
  const availabilityPayloads = useLoadableState<AvailabilityPayloads[]>([]);
  const carouselVehicleAvailability = useLoadableState<AvailabilityPayloads[]>(
    []
  );
  const pageAvailability = useLoadableState<PageAvailabilityData>({});
  const recentVehicleAvailability = useLoadableState<AvailabilityPayloads[]>(
    []
  );
  const suggestedVehicleAvailability = useLoadableState<AvailabilityPayloads[]>(
    []
  );

  const value = useMemo<AvailabilityContextType>(
    () => ({
      availabilityPayloads,
      carouselVehicleAvailability,
      pageAvailability,
      recentVehicleAvailability,
      suggestedVehicleAvailability
    }),
    [
      availabilityPayloads,
      carouselVehicleAvailability,
      pageAvailability,
      recentVehicleAvailability,
      suggestedVehicleAvailability
    ]
  );

  return (
    <AvailabilityContext.Provider value={value}>
      {children}
    </AvailabilityContext.Provider>
  );
}

AvailabilityContext.displayName = 'AvailabilityContextProvider';

export default AvailabilityContext;
